import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Platform } from '@ionic/angular';

// Narrow type for the deferred prompt event used on Android/Chrome
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice?: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>;
};

export type InstallState =
  | 'idle'
  | 'prompted'
  | 'in-progress'
  | 'installed'
  | 'dismissed'
  | 'error';

@Injectable({ providedIn: 'root' })
export class PwaService {
  // Consumers subscribe to decide whether to show the bottom sheet
  public readonly showInstallPrompt = new BehaviorSubject<boolean>(false);
  // Track install flow state for inline loader/messages in the UI
  public readonly installState$ = new BehaviorSubject<InstallState>('idle');

  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private eligibleEnv = false;
  private readonly installedFlagKey = 'spitas.pwaInstalled';
  private readonly sessionSeenKey = 'spitas.pwaInstalledSeenSession';

  constructor(private platform: Platform) {
    // Check environment early (constructor requirement)
    this.eligibleEnv = this.isBrowser() && !this.isStandalone();
  }

  /** Call once (e.g., from AppComponent.ngOnInit) to set up listeners. */
  init(): void {
    if (!this.eligibleEnv) {
      return;
    }

    // If the app has been installed previously, optionally show the installed message once per session.
    try {
      const alreadyInstalled = !!localStorage.getItem(this.installedFlagKey);
      const seenThisSession = !!sessionStorage.getItem(this.sessionSeenKey);
      if (alreadyInstalled && !seenThisSession) {
        this.installState$.next('installed');
        this.showInstallPrompt.next(true);
        sessionStorage.setItem(this.sessionSeenKey, '1');
      }
    } catch {}

    // iOS does not fire beforeinstallprompt; show our custom sheet immediately
    if (this.platform.is('ios')) {
      this.showInstallPrompt.next(true);
      return;
    }

    // Android / Chrome: intercept and keep the event so we can trigger it later
    window.addEventListener('beforeinstallprompt', (event: Event) => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.installState$.next('prompted');
      this.showInstallPrompt.next(true);
    });

    // Fallback: Some Android Chrome environments (dev/incognito/not installable) won't fire the event.
    // Still show our sheet so users see instructions or can retry later.
    if (this.platform.is('android')) {
      setTimeout(() => {
        if (!this.deferredPrompt) {
          this.showInstallPrompt.next(true);
        }
      }, 1500);
    }

    // If app gets installed via any means, reflect that in state
    window.addEventListener('appinstalled', () => {
      this.installState$.next('installed');
      try {
        localStorage.setItem(this.installedFlagKey, String(Date.now()));
        sessionStorage.setItem(this.sessionSeenKey, '1');
      } catch {}
      // Do not auto-hide here; keep the sheet visible so user sees the message
      // User will close the sheet manually.
      this.clearPrompt();
    });
  }

  /** Trigger the saved install prompt (Android/Chrome). */
  async installPwa(): Promise<void> {
    if (!this.deferredPrompt) {
      // Nothing to prompt (likely iOS or already installed)
      return;
    }
    try {
      this.installState$.next('in-progress');
      await this.deferredPrompt.prompt();
      if (this.deferredPrompt.userChoice) {
        const res = await this.deferredPrompt.userChoice;
        if (res.outcome === 'accepted') {
          this.installState$.next('installed');
          try {
            localStorage.setItem(this.installedFlagKey, String(Date.now()));
            sessionStorage.setItem(this.sessionSeenKey, '1');
          } catch {}
          // Keep the install sheet open so the user can read the success message
        } else {
          this.installState$.next('dismissed');
        }
      }
    } finally {
      // If prompt threw or finished, clear stored event either way
      if (!this.deferredPrompt) return;
      this.clearPrompt();
    }
  }

  /** Whether we currently have a deferred install prompt available (Android/Chrome). */
  canPrompt(): boolean {
    return !!this.deferredPrompt;
  }

  /** Hide the custom prompt (used by Close button or after install). */
  hideInstallPrompt(): void {
    this.showInstallPrompt.next(false);
    // reset state for next time
    this.installState$.next('idle');
    try {
      sessionStorage.setItem(this.sessionSeenKey, '1');
    } catch {}
  }

  private clearPrompt(): void {
    this.deferredPrompt = null;
    // Do not auto-hide the sheet here; visibility is controlled by the user.
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }

  private isStandalone(): boolean {
    const nav: any = typeof navigator !== 'undefined' ? navigator : {};
    const matchStandalone =
      typeof window !== 'undefined' &&
      typeof (window as any).matchMedia === 'function' &&
      (window as any).matchMedia('(display-mode: standalone)').matches;
    const iosStandalone = !!nav.standalone; // Safari iOS
    return matchStandalone || iosStandalone;
  }
}
